import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { auth, db, functions, ensureAppCheck } from '../firebase';
import {
    onAuthStateChanged,
    signOut,
    signInWithEmailAndPassword,
    sendPasswordResetEmail,
    GoogleAuthProvider,
    OAuthProvider,
    signInWithPopup,
    getRedirectResult,
    getAdditionalUserInfo,
    getAuth
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { initializeRoleService, hasPermission, PERMISSIONS } from '../utils/rolePermissions';
import {
    NO_ACCOUNT_MESSAGE,
    FINISH_ACCOUNT_MESSAGE,
    NOT_PROVISIONED_MESSAGE,
    DEACTIVATED_MESSAGE
} from '../utils/authErrors';
import { isSelfServeProfile, isSetupEligibleSession } from '../utils/selfServe';
import { orgAccessCompanyIds } from '../utils/orgAccess';
import { isProvisionedProfile } from '../utils/provisionedProfile';

const AuthContext = createContext(null);

// A Google session with no profile that may finish a self-serve account
// (isSetupEligibleSession). It is kept signed in, but holds no role and no
// company: the route guards send it to /signup/finish, CompanyContext and the
// role service skip it, and firestore.rules give it nothing. No profile is
// written here; completeSelfServeAccount provisions it on the server.
const setupSessionUser = (user) => ({ ...user, role: null, needsSetup: true });

// After a self-serve account is provisioned: pick up the new profile with a
// fresh ID token and a full reload, so every context starts from the
// provisioned session. Link mode signs in with the password just chosen.
const enterNewAccount = async ({ email, password } = {}) => {
    if (email && password) {
        await signInWithEmailAndPassword(auth, email, password);
    }
    if (auth.currentUser) {
        await auth.currentUser.getIdToken(true);
    }
    window.location.assign('/admin?welcome=1');
};

// The two public self-serve callables. Both enforce App Check and consume
// limited-use tokens, so App Check is started first. The payload carries
// personal details (and, for the link, a password): never log it.
const callSelfServe = async (name, payload) => {
    ensureAppCheck();
    const callable = httpsCallable(functions, name, { limitedUseAppCheckTokens: true });
    const result = await callable(payload);
    return result.data;
};

// Undo a sign-in that has no SolushipX profile. When the sign-in itself just
// created the Firebase Auth account (a first-time Google/Microsoft popup), delete
// that account too: otherwise it lingers as an orphan login, and a later invite
// for the same email fails with "a user with this email address already exists".
// An Auth account that already existed (e.g. one being set up by an admin) is
// only signed out, never deleted.
const refuseUnprovisionedSignIn = async (result) => {
    const isNewAuthAccount = Boolean(result && getAdditionalUserInfo(result)?.isNewUser);
    if (isNewAuthAccount) {
        try {
            await result.user.delete();
        } catch (deleteError) {
            console.warn('[Auth] Could not remove the unprovisioned sign-in account:', deleteError?.code || deleteError);
        }
    }
    await signOut(auth).catch(() => {});
};

// Debug flag - set to true for verbose console logging
const DEBUG = false;

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    // True while a Google/Microsoft popup sign-in is being finalized. The auth
    // observer then leaves a profile-less session to finalizeFederatedSignIn,
    // which also removes the Auth account the popup just created (signing out
    // first would race it and strand that orphan account).
    const federatedSignInPending = useRef(false);
    const [userRole, setUserRole] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [initialized, setInitialized] = useState(false);
    const [roleServiceInitialized, setRoleServiceInitialized] = useState(false);

    // User emulation state - allows admins to "view as" another user
    // Initialize from localStorage for persistence across page loads
    const [emulatedUser, setEmulatedUser] = useState(() => {
        try {
            const stored = localStorage.getItem('solushipx_emulation');
            if (stored) {
                const data = JSON.parse(stored);
                let restoredUser = data.emulatedUser || null;

                // Migrate: If we have connectedCompanyIds in storage but emulatedUser doesn't have them,
                // update the connectedCompanies to include all companies
                if (restoredUser && data.connectedCompanyIds && data.connectedCompanyIds.length > 0) {
                    restoredUser = {
                        ...restoredUser,
                        connectedCompanies: { companies: data.connectedCompanyIds }
                    };
                    DEBUG && console.log('🎭 [AuthContext] Restored emulated user with all connected companies:', data.connectedCompanyIds);
                }

                return restoredUser;
            }
        } catch (e) {
            DEBUG && console.warn('Error loading emulation state:', e);
        }
        return null;
    });
    const [emulatedUserRole, setEmulatedUserRole] = useState(() => {
        try {
            const stored = localStorage.getItem('solushipx_emulation');
            if (stored) {
                const data = JSON.parse(stored);
                return data.emulatedUserRole || null;
            }
        } catch (e) { }
        return null;
    });
    const [isEmulating, setIsEmulating] = useState(() => {
        try {
            const stored = localStorage.getItem('solushipx_emulation');
            if (stored) {
                const data = JSON.parse(stored);
                return data.isEmulating || false;
            }
        } catch (e) { }
        return false;
    });
    // Lazy-init actualUser from localStorage so the emulation banner shows the real
    // admin immediately after a reload instead of flashing "Admin: Unknown" (#17).
    const [actualUser, setActualUser] = useState(() => {
        try {
            const stored = localStorage.getItem('solushipx_emulation');
            if (stored) {
                const data = JSON.parse(stored);
                if (data.isEmulating && data.actualUserEmail) {
                    return { email: data.actualUserEmail };
                }
            }
        } catch (e) { }
        return null;
    });
    // Initialize actualUserRole from localStorage for page reload survival
    const [actualUserRole, setActualUserRole] = useState(() => {
        try {
            const stored = localStorage.getItem('solushipx_emulation');
            if (stored) {
                const data = JSON.parse(stored);
                // Return the stored actual user role if emulating
                if (data.isEmulating && data.actualUserRole) {
                    return data.actualUserRole;
                }
            }
        } catch (e) { }
        return null;
    });

    // Enter a needsSetup session. It can never be emulating (only staff
    // emulate), so drop any emulation state an earlier admin session left in
    // this browser: otherwise the guards would see the stale emulated user
    // instead of this session.
    const enterSetupSession = (firebaseUser) => {
        try {
            if (localStorage.getItem('solushipx_emulation')) {
                localStorage.removeItem('solushipx_emulation');
            }
        } catch (e) { }
        setIsEmulating(false);
        setEmulatedUser(null);
        setEmulatedUserRole(null);
        setActualUser(null);
        setActualUserRole(null);
        const setupUser = setupSessionUser(firebaseUser);
        setUser(setupUser);
        setUserRole(null);
        return setupUser;
    };

    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                try {
                    // Force refresh the token to get latest custom claims
                    const idTokenResult = await user.getIdTokenResult(true);

                    // Fetch user data from Firestore for all users
                    const userDocRef = doc(db, "users", user.uid);
                    const docSnap = await getDoc(userDocRef);

                    if (docSnap.exists()) {
                        const userDataFromFirestore = docSnap.data();

                        // Hard stop for deactivated / suspended / soft-deleted accounts.
                        // The server already disables their Auth login + revokes refresh
                        // tokens, but an already-issued ID token stays valid until it
                        // expires (~1h) — so we also force a sign-out here on session
                        // restore so they cannot keep using the app. (Missing status === active.)
                        const acctStatus = userDataFromFirestore.status;
                        if (acctStatus === 'inactive' || acctStatus === 'suspended' || acctStatus === 'deleted' || userDataFromFirestore.deleted === true) {
                            console.warn(`[Auth] Account ${user.uid} is "${acctStatus || 'deleted'}" — signing out.`);
                            await signOut(auth);
                            setUser(null);
                            setUserRole(null);
                            setLoading(false);
                            setInitialized(true);
                            return;
                        }

                        // A profile attached to nothing (no platform role, no
                        // company) gets nothing from the rules: end the session.
                        // A popup sign-in in flight is refused by its own handler.
                        if (!isProvisionedProfile(userDataFromFirestore)) {
                            if (!federatedSignInPending.current) {
                                console.warn(`[Auth] Profile ${user.uid} has no company or platform role — signing out.`);
                                await signOut(auth);
                            }
                            setUser(null);
                            setUserRole(null);
                            setLoading(false);
                            setInitialized(true);
                            return;
                        }

                        // Set role based on claims first, then Firestore fallback
                        if (idTokenResult.claims.superadmin === true) {
                            setUserRole('superadmin');
                        } else if (idTokenResult.claims.admin === true) {
                            setUserRole('admin');
                        } else {
                            setUserRole(userDataFromFirestore.role || 'user');
                        }

                        // Always add Firestore data to user object
                        const fullUserData = {
                            ...user,
                            firstName: userDataFromFirestore.firstName,
                            companyId: userDataFromFirestore.companyId,
                            role: userDataFromFirestore.role,
                            connectedCompanies: userDataFromFirestore.connectedCompanies,
                            // Customer-level access scope. Default-OFF: absent =>
                            // full company access (unchanged behavior).
                            connectedCustomers: userDataFromFirestore.connectedCustomers || { customers: [] },
                            // Carry hierarchy + org access on the in-memory user so a real
                            // session matches emulation and HierarchyContext reads it without
                            // a users/{uid} re-query (#11).
                            connectedOrganizations: userDataFromFirestore.connectedOrganizations || null,
                            // Server-written organization access grant (read-only here).
                            orgAccess: userDataFromFirestore.orgAccess || null,
                            // Server-written: reads go to the cost-free shipmentViews (utils/shipmentSource).
                            costRestricted: userDataFromFirestore.costRestricted === true,
                            hierarchyAccess: userDataFromFirestore.hierarchyAccess || null,
                            // Surfaced so route guards can defend-in-depth against a
                            // mid-session deactivation.
                            status: userDataFromFirestore.status || 'active',
                            deleted: userDataFromFirestore.deleted === true,
                            // Whether this profile is a self-serve tenant (the server-owned
                            // flag the rules and callables trust): the menus decide
                            // membership from it without waiting for the company (useSelfServe).
                            selfServeTenant: isSelfServeProfile(userDataFromFirestore)
                        };
                        setUser(fullUserData);

                        // If we're emulating, set the actual user from the logged-in Firebase user
                        let emulationActive = false;
                        const storedEmulation = localStorage.getItem('solushipx_emulation');
                        if (storedEmulation) {
                            try {
                                const emulationData = JSON.parse(storedEmulation);
                                emulationActive = emulationData.isEmulating === true;
                                if (emulationData.isEmulating) {
                                    setActualUser(fullUserData);
                                    // Set actual role based on claims
                                    if (idTokenResult.claims.superadmin === true) {
                                        setActualUserRole('superadmin');
                                    } else if (idTokenResult.claims.admin === true) {
                                        setActualUserRole('admin');
                                    } else {
                                        setActualUserRole(userDataFromFirestore.role || 'user');
                                    }
                                }
                            } catch (e) {
                                DEBUG && console.warn('Error restoring emulation state:', e);
                            }
                        }

                        // Several screens find the user's companies by users.email, so keep
                        // it in step with a verified sign-in email that changed outside the
                        // app. firestore.rules lets the user set only this value (their own
                        // verified token email). Own doc only, never while emulating;
                        // best-effort, it must not hold up sign-in.
                        const profileEmail = userDataFromFirestore.email;
                        if (!emulationActive && user.emailVerified && typeof user.email === 'string' && user.email
                            && user.email.toLowerCase() !== (typeof profileEmail === 'string' ? profileEmail : '').toLowerCase()) {
                            updateDoc(userDocRef, { email: user.email, updatedAt: new Date() })
                                .catch((syncError) => console.warn('[Auth] Could not sync the profile email:', syncError?.code || syncError));
                        }
                    } else if (isSetupEligibleSession(user, idTokenResult)) {
                        // A first-time Google session that may finish a self-serve
                        // account: keep it, as needsSetup (see setupSessionUser).
                        enterSetupSession(user);
                    } else {
                        // Any other signed-in Auth account with no SolushipX profile
                        // is not a user of the app: end the session instead of
                        // handing it an empty Company Admin shell. Custom claims do
                        // not change this — firestore.rules and the callables' tenant
                        // scope read only users/{uid}, so a claims-only session could
                        // reach nothing.
                        if (!federatedSignInPending.current) {
                            console.warn(`[Auth] No SolushipX profile for ${user.uid} — signing out.`);
                            await signOut(auth);
                        }
                        setUser(null);
                        setUserRole(null);
                    }
                } catch (error) {
                    console.error('Error in auth state change:', error);
                    setUser(null);
                    setUserRole(null);
                }
            } else {
                setUser(null);
                setUserRole(null);
            }
            setLoading(false);
            setInitialized(true);
        });

        return () => unsubscribe();
    }, []);

    // Legacy: handle any pending redirect result (kept as safety net for old sessions)
    useEffect(() => {
        const handleRedirectResult = async () => {
            try {
                const result = await getRedirectResult(auth);
                if (result && result.user) {
                    DEBUG && console.log('Redirect sign-in result received for:', result.user.email);
                    const userDoc = await getDoc(doc(db, 'users', result.user.uid));
                    if (!userDoc.exists()) {
                        // No profile: never self-provision one on the client. A
                        // Google session that may finish a self-serve account is
                        // left to the auth observer (needsSetup); any other is
                        // refused.
                        const tokenResult = await result.user.getIdTokenResult();
                        if (!isSetupEligibleSession(result.user, tokenResult)) {
                            await refuseUnprovisionedSignIn(result);
                            setError(NO_ACCOUNT_MESSAGE);
                        }
                    } else if (!isProvisionedProfile(userDoc.data())) {
                        await signOut(auth).catch(() => {});
                        setError(NOT_PROVISIONED_MESSAGE);
                    } else {
                        await setDoc(doc(db, 'users', result.user.uid), {
                            lastLogin: new Date(),
                            updatedAt: new Date()
                        }, { merge: true });
                    }
                }
            } catch (error) {
                // Expected on non-redirect loads — silently ignore
            }
        };

        handleRedirectResult();
    }, []);

    // Initialize the role service ONCE THE USER IS AUTHENTICATED.
    //
    // This previously ran on bare mount ([] deps), which raced Firebase Auth's session
    // restore: the `roles` / `rolePermissions` rules require an authenticated reader,
    // so the load was denied ("Missing or insufficient permissions") and — because the
    // failure was memoised in roleService.init — the database-backed permissions never
    // loaded for the whole session.
    useEffect(() => {
        // A needsSetup session reads nothing: the roles collections are for
        // provisioned users only.
        if (!user || user.needsSetup) return undefined;

        let cancelled = false;
        const initRoleService = async () => {
            try {
                await initializeRoleService();
                if (!cancelled) setRoleServiceInitialized(true);
                DEBUG && console.log('🔐 Role service initialized successfully');
            } catch (error) {
                DEBUG && console.warn('⚠️ Role service initialization failed, using fallback:', error);
                if (!cancelled) setRoleServiceInitialized(true); // Still proceed with hardcoded roles
            }
        };

        initRoleService();
        return () => { cancelled = true; };
    }, [user]);

    const login = async (email, password) => {
        // NOTE: intentionally does NOT toggle the app-wide `loading` flag. That flag
        // gates rendering of the entire app ({!loading && children}); flipping it here
        // unmounts the <Login> form mid-await, wiping its local error/field state and
        // racing onAuthStateChanged on redirect. Sign-in pending state lives locally in
        // the Login component instead.
        try {
            setError(null);
            const result = await signInWithEmailAndPassword(auth, email, password);

            // Fetch user data from Firestore
            const userDoc = await getDoc(doc(db, 'users', result.user.uid));
            let userWithRole;

            if (userDoc.exists()) {
                const userData = userDoc.data();
                DEBUG && console.log('Login - User data from Firestore:', userData);

                // Refuse sign-in for deactivated / suspended / soft-deleted accounts.
                // (A disabled Auth account already fails signInWithEmailAndPassword
                // above; this also covers a status set without the Auth disable.)
                const acctStatus = userData.status;
                if (acctStatus === 'inactive' || acctStatus === 'suspended' || acctStatus === 'deleted' || userData.deleted === true) {
                    await signOut(auth);
                    throw new Error(DEACTIVATED_MESSAGE);
                }

                // A profile attached to no company and no platform role.
                if (!isProvisionedProfile(userData)) {
                    await signOut(auth);
                    throw new Error(NOT_PROVISIONED_MESSAGE);
                }

                // Create enriched user object with role
                userWithRole = {
                    ...result.user,
                    companyId: userData.companyId,
                    role: userData.role,
                    connectedCompanies: userData.connectedCompanies,
                    // Customer-level access scope. Default-OFF: absent =>
                    // full company access (unchanged behavior).
                    connectedCustomers: userData.connectedCustomers || { customers: [] },
                    connectedOrganizations: userData.connectedOrganizations || null,
                    orgAccess: userData.orgAccess || null,
                    // Server-written: reads go to the cost-free shipmentViews (utils/shipmentSource).
                    costRestricted: userData.costRestricted === true,
                    hierarchyAccess: userData.hierarchyAccess || null,
                    // Surfaced so the route guards' status check has data on this path too.
                    status: userData.status || 'active',
                    deleted: userData.deleted === true,
                    selfServeTenant: isSelfServeProfile(userData)
                };

                // Set user with additional data from Firestore
                setUser(userWithRole);
                setUserRole(userData.role);

                // Update lastLogin
                await setDoc(doc(db, 'users', result.user.uid), {
                    lastLogin: new Date(),
                    updatedAt: new Date()
                }, { merge: true });
            } else {
                // A password login whose Auth account has no SolushipX profile
                // (custom claims included: the rules read only users/{uid}). It
                // is refused; the owner finishes it by requesting an activation
                // link with this email, which adopts the Auth account.
                await signOut(auth);
                throw new Error(FINISH_ACCOUNT_MESSAGE);
            }

            // Return the enriched user object with role for redirect logic
            return userWithRole;
        } catch (err) {
            console.error('Login error:', err);
            setError(err.message);
            throw err;
        }
    };

    // Self-serve accounts: the browser never creates an Auth account. The
    // server creates (or adopts) it and provisions the company in one go.
    const requestSelfServeAccount = (details) => callSelfServe('requestSelfServeAccount', details);
    const completeSelfServeAccount = (payload) => callSelfServe('completeSelfServeAccount', payload);

    const logout = async () => {
        try {
            DEBUG && console.log('AuthContext - Starting logout process');
            setError(null);
            setLoading(true); // Set loading state while logging out

            // Clear emulation state first (if any)
            if (isEmulating) {
                DEBUG && console.log('AuthContext - Clearing emulation state before logout');
                setEmulatedUser(null);
                setEmulatedUserRole(null);
                setIsEmulating(false);
                setActualUser(null);
                setActualUserRole(null);
            }

            // Sign out from Firebase
            await signOut(auth);
            DEBUG && console.log('AuthContext - Firebase signOut successful');

            // Clear local state
            setUser(null);
            setUserRole(null);

            // Clear company data from localStorage
            localStorage.removeItem('solushipx_company_data');
            localStorage.removeItem('solushipx_company_id_for_address');
            // Clear emulation data from localStorage
            localStorage.removeItem('solushipx_emulation');
            DEBUG && console.log('AuthContext - Cleared company and emulation data from localStorage');

            // Wait for a moment to ensure state is cleared
            await new Promise(resolve => setTimeout(resolve, 100));

            DEBUG && console.log('AuthContext - User state cleared');
        } catch (err) {
            console.error('AuthContext - Logout error:', err);
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const resetPassword = async (email) => {
        try {
            setError(null);
            // The reset page's "Continue" button brings them back to sign in.
            await sendPasswordResetEmail(auth, email, { url: `${window.location.origin}/login?reset=1` });
        } catch (err) {
            // Don't reveal whether an account exists for this email.
            if (err?.code === 'auth/user-not-found') return;
            setError(err.message);
            throw err;
        }
    };

    // Shared post-sign-in handling for federated (Google / Microsoft) logins.
    // Signs in invited users (their users/{uid} profile already exists), enforces
    // the account-status guard, and returns the role-enriched user object. A
    // first-time Google sign-in that may finish a self-serve account is kept as
    // needsSetup (no profile is written); every other sign-in with no profile is
    // refused.
    const finalizeFederatedSignIn = async (result) => {
        const userDoc = await getDoc(doc(db, 'users', result.user.uid));
        if (!userDoc.exists()) {
            const tokenResult = await result.user.getIdTokenResult();
            if (isSetupEligibleSession(result.user, tokenResult)) {
                return enterSetupSession(result.user);
            }
            await refuseUnprovisionedSignIn(result);
            throw new Error(NO_ACCOUNT_MESSAGE);
        }

        const userData = userDoc.data();

        // Refuse sign-in for deactivated / suspended / soft-deleted accounts.
        const acctStatus = userData.status;
        if (acctStatus === 'inactive' || acctStatus === 'suspended' || acctStatus === 'deleted' || userData.deleted === true) {
            await signOut(auth);
            throw new Error(DEACTIVATED_MESSAGE);
        }

        // An orphan profile an earlier first-time sign-in self-created (role
        // 'user', no company). The Auth account predates this sign-in, so it is
        // only signed out, never deleted.
        if (!isProvisionedProfile(userData)) {
            await signOut(auth);
            throw new Error(NOT_PROVISIONED_MESSAGE);
        }

        const userWithRole = {
            ...result.user,
            companyId: userData.companyId,
            role: userData.role,
            connectedCompanies: userData.connectedCompanies,
            // Customer-level access scope. Default-OFF: absent =>
            // full company access (unchanged behavior).
            connectedCustomers: userData.connectedCustomers || { customers: [] },
            connectedOrganizations: userData.connectedOrganizations || null,
            orgAccess: userData.orgAccess || null,
            // Server-written: reads go to the cost-free shipmentViews (utils/shipmentSource).
            costRestricted: userData.costRestricted === true,
            hierarchyAccess: userData.hierarchyAccess || null,
            // Surfaced so the route guards' status check has data on this path too.
            status: userData.status || 'active',
            deleted: userData.deleted === true,
            selfServeTenant: isSelfServeProfile(userData)
        };
        // Set the user here (not just the role) so the route guards see an
        // authenticated user immediately on redirect, instead of racing the async
        // onAuthStateChanged handler and bouncing back to /login.
        setUser(userWithRole);
        setUserRole(userData.role);

        // Update lastLogin without clobbering other fields.
        await setDoc(doc(db, 'users', result.user.uid), {
            lastLogin: new Date(),
            updatedAt: new Date()
        }, { merge: true });

        return userWithRole;
    };

    const signInWithGoogle = async () => {
        try {
            setError(null);
            const provider = new GoogleAuthProvider();
            let result;

            federatedSignInPending.current = true;
            try {
                result = await signInWithPopup(auth, provider);
            } catch (popupError) {
                if (popupError.code === 'auth/popup-blocked' || popupError.code === 'auth/popup-closed-by-user') {
                    const embeddedMsg = 'Google sign-in is not available in this browser. Please use email and password to sign in.';
                    setError(embeddedMsg);
                    throw new Error(embeddedMsg);
                }
                throw popupError;
            }

            return await finalizeFederatedSignIn(result);
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            federatedSignInPending.current = false;
        }
    };

    const signInWithMicrosoft = async () => {
        try {
            setError(null);
            const provider = new OAuthProvider('microsoft.com');
            let result;

            federatedSignInPending.current = true;
            try {
                result = await signInWithPopup(auth, provider);
            } catch (popupError) {
                if (popupError.code === 'auth/popup-blocked' || popupError.code === 'auth/popup-closed-by-user') {
                    const embeddedMsg = 'Microsoft sign-in is not available in this browser. Please use email and password to sign in.';
                    setError(embeddedMsg);
                    throw new Error(embeddedMsg);
                }
                throw popupError;
            }

            return await finalizeFederatedSignIn(result);
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            federatedSignInPending.current = false;
        }
    };

    // ============================================
    // USER EMULATION FUNCTIONS
    // ============================================

    /**
     * Get numeric level for role hierarchy comparison
     * Higher number = more privileges
     * @param {string} role - Role identifier
     * @returns {number} - Hierarchy level
     */
    const getRoleLevel = (role) => {
        const levels = {
            'superadmin': 100,
            'admin': 60,
            'org_admin': 50, // a Company Admin across its org's companies: below admin
            'user': 40,
            'accounting': 20,
            'company_staff': 20,
            'manufacturer': 20,
            'manufacturer_staff': 15,
            'customer': 10 // End-user customer with limited admin access
        };
        return levels[role] || 0;
    };

    /**
     * Check if current role can emulate target role
     * @param {string} currentRole - The actual user's role
     * @param {string} targetRole - The role to emulate
     * @returns {boolean} - True if emulation is allowed
     */
    const canEmulateRole = (currentRole, targetRole) => {
        return getRoleLevel(currentRole) > getRoleLevel(targetRole);
    };

    /**
     * Get list of roles that the current user can emulate
     * @param {string} currentRole - The actual user's role
     * @returns {string[]} - Array of role IDs that can be emulated
     */
    const getEmulatableRoles = (currentRole) => {
        const allRoles = ['superadmin', 'org_admin', 'admin', 'user', 'accounting', 'company_staff', 'manufacturer', 'manufacturer_staff', 'customer'];
        const currentLevel = getRoleLevel(currentRole);
        return allRoles.filter(role => getRoleLevel(role) < currentLevel);
    };

    /**
     * Start emulating another user
     * @param {string} userId - Firestore user ID to emulate
     * @returns {Promise<Object>} - { success: boolean, redirectTo: string|null }
     */
    const startEmulation = async (userId) => {
        try {
            // Only superadmins and admins can emulate
            if (userRole !== 'superadmin' && userRole !== 'admin') {
                DEBUG && console.warn('User does not have permission to emulate');
                return { success: false, redirectTo: null };
            }

            // Fetch the user to emulate
            const userDocRef = doc(db, 'users', userId);
            const userDocSnap = await getDoc(userDocRef);

            if (!userDocSnap.exists()) {
                console.error('User to emulate not found');
                return { success: false, redirectTo: null };
            }

            const userData = userDocSnap.data();
            const emulatedRole = userData.role || 'user';

            // Store actual user before emulating
            setActualUser(user);
            setActualUserRole(userRole);

            // Set emulated user
            const emulatedUserData = {
                uid: userId,
                email: userData.email,
                displayName: `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || userData.email,
                firstName: userData.firstName,
                lastName: userData.lastName,
                companyId: userData.companyId,
                role: emulatedRole,
                connectedCompanies: userData.connectedCompanies,
                // Carry the emulated user's customer restriction so the by-uid emulation
                // path scopes data identically to that user's real login (#3/#14).
                connectedCustomers: userData.connectedCustomers || { customers: [] },
                connectedOrganizations: userData.connectedOrganizations,
                orgAccess: userData.orgAccess || null,
                // Server-written: reads go to the cost-free shipmentViews (utils/shipmentSource).
                costRestricted: userData.costRestricted === true,
                hierarchyAccess: userData.hierarchyAccess,
                // The emulated profile's self-serve flag, so the preview shows what
                // that member's menus show (useSelfServe).
                selfServeTenant: isSelfServeProfile(userData)
            };

            // #4: isolate the admin's company selection from the emulated session so the
            // localStorage 'selected company' does not bleed across emulate start/stop.
            const adminSelectedCompany = localStorage.getItem('solushipx_selected_company_id');
            if (adminSelectedCompany) localStorage.setItem('solushipx_admin_selected_company', adminSelectedCompany);
            localStorage.removeItem('solushipx_selected_company_id');

            setEmulatedUser(emulatedUserData);
            setEmulatedUserRole(emulatedRole);
            setIsEmulating(true);

            // Persist to localStorage for page reload survival
            // IMPORTANT: Include actualUserRole so permission checks work after reload
            localStorage.setItem('solushipx_emulation', JSON.stringify({
                isEmulating: true,
                emulatedUser: emulatedUserData,
                emulatedUserRole: emulatedRole,
                actualUserEmail: user?.email,
                actualUserRole: userRole // Store the actual user's role for permission checks
            }));

            DEBUG && console.log('🎭 Started emulating user:', userData.email, 'with role:', emulatedRole);

            // Determine redirect based on emulated role's permissions
            const emulatedHasAdminAccess = hasPermission(emulatedRole, PERMISSIONS.VIEW_ADMIN_DASHBOARD);
            const redirectTo = '/admin'; // All roles route to the /admin universe

            DEBUG && console.log('🔄 Emulated role admin access:', emulatedHasAdminAccess, '-> redirect to:', redirectTo);

            return { success: true, redirectTo, hasAdminAccess: emulatedHasAdminAccess };
        } catch (error) {
            console.error('Error starting emulation:', error);
            return { success: false, redirectTo: null };
        }
    };

    /**
     * Start emulating a role (not a specific user)
     * Creates a synthetic user with the specified role and company context
     * @param {string} targetRole - The role to emulate
     * @param {Object} companyData - The company context for the emulation
     * @param {Object} customerData - Optional customer context for customer role emulation
     * @returns {Promise<Object>} - { success: boolean, redirectTo: string|null, hasAdminAccess: boolean }
     */
    const startRoleEmulation = async (targetRole, companyData, customerData = null, userData = null) => {
        try {
            const effectiveActualRole = actualUserRole || userRole;

            // Validate that user can emulate this role
            if (!canEmulateRole(effectiveActualRole, targetRole)) {
                DEBUG && console.warn('Cannot emulate a role at or above your level:', effectiveActualRole, '->', targetRole);
                return { success: false, redirectTo: null, reason: 'same_or_higher_role' };
            }

            // Validate company data is present
            if (!companyData?.companyID) {
                DEBUG && console.warn('Cannot emulate without company data');
                return { success: false, redirectTo: null, reason: 'no_company' };
            }

            // Store actual user before emulating
            setActualUser(user);
            setActualUserRole(userRole);

            // Build connected customers array for customer role.
            // This is critical for filtering - views look for connectedCustomers.customers.
            // Shipments reference customers in two formats — the Firestore doc id
            // (`customerId`) and the human customerID code (`customerID` like
            // "ICAL-XYZ"). Include BOTH forms so downstream filters match
            // regardless of which field a given shipment stored.
            let connectedCustomersData = null;
            // Build customer scope for emulation whenever customerData carries it —
            // not only for the 'customer' role. A customer-restricted user of ANY role
            // (e.g. a manufacturer limited to CHSTF) must emulate WITH the restriction,
            // matching real-login behavior where AuthContext loads connectedCustomers
            // for every role. handleEmulateUser passes customerData.connectedCustomers
            // (both id forms) for any restricted user.
            if (customerData && (targetRole === 'customer' || customerData.connectedCustomers)) {
                const customerIds = customerData.connectedCustomers || [
                    customerData.id,
                    customerData.customerID
                ].filter(Boolean);
                // De-dupe in case id === customerID
                connectedCustomersData = { customers: Array.from(new Set(customerIds)) };
            }

            // Determine display names - prefer actual user data if provided
            const hasUserData = userData && (userData.firstName || userData.lastName || userData.email);
            const displayFirstName = hasUserData ? userData.firstName : targetRole.charAt(0).toUpperCase() + targetRole.slice(1);
            const displayLastName = hasUserData ? userData.lastName : 'Role';
            const displayName = hasUserData
                ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || userData.email
                : `${targetRole.charAt(0).toUpperCase() + targetRole.slice(1)} Role`;
            const displayEmail = hasUserData ? userData.email : `${targetRole}@emulation.local`;

            // Create synthetic emulated user with the target role and company context
            // Customer emulation MUST be scoped to a single company — the customer's
            // parent company only. Customers do not have visibility into sibling
            // companies in the hierarchy, even if the company itself does.
            // For non-customer role emulation, use ALL connected companies if provided,
            // otherwise fall back to single company.
            const allConnectedCompanyIds = targetRole === 'customer'
                ? (customerData?.companyID ? [customerData.companyID] : (companyData?.companyID ? [companyData.companyID] : []))
                : (companyData?.connectedCompanyIds || (companyData?.companyID ? [companyData.companyID] : []));

            DEBUG && console.log('🎭 [startRoleEmulation] Company data received:', {
                companyData,
                connectedCompanyIds: companyData?.connectedCompanyIds,
                allConnectedCompanyIds
            });

            const syntheticEmulatedUser = {
                uid: `role_emulation_${targetRole}_${customerData?.id || companyData?.companyID || 'no_company'}`,
                email: displayEmail,
                displayName: displayName,
                firstName: displayFirstName,
                lastName: displayLastName,
                companyId: companyData?.companyID || null,
                role: targetRole,
                connectedCompanies: allConnectedCompanyIds.length > 0 ? { companies: allConnectedCompanyIds } : null,
                // CRITICAL: connectedCustomers must be set for customer filtering to work
                connectedCustomers: connectedCustomersData,
                // Carry the emulated user's hierarchy + org access so emulation matches
                // their real login's multi-company / org visibility instead of collapsing
                // to the primary company (#5/#11). Falls back to null for pure role emulation.
                connectedOrganizations: userData?.connectedOrganizations || null,
                orgAccess: userData?.orgAccess || null,
                // Server-written: reads go to the cost-free shipmentViews (utils/shipmentSource).
                costRestricted: userData?.costRestricted === true,
                hierarchyAccess: userData?.hierarchyAccess || null,
                // Flag to indicate this is a role emulation, not a user emulation
                isRoleEmulation: true,
                // Indicate if we're emulating a specific user (vs just a role)
                isUserEmulation: hasUserData,
                emulatedCompanyName: companyData?.name || 'Unknown Company',
                // Customer role specific data
                customerId: customerData?.id || null,
                customerName: customerData?.name || null,
                customerData: customerData || null,
                // Original user data for display
                emulatedUserName: displayName,
                emulatedUserEmail: displayEmail
            };

            // #4: isolate the admin's company selection from the emulated session so the
            // localStorage 'selected company' does not bleed across emulate start/stop.
            const adminSelectedCompany = localStorage.getItem('solushipx_selected_company_id');
            if (adminSelectedCompany) localStorage.setItem('solushipx_admin_selected_company', adminSelectedCompany);
            localStorage.removeItem('solushipx_selected_company_id');

            setEmulatedUser(syntheticEmulatedUser);
            setEmulatedUserRole(targetRole);
            setIsEmulating(true);

            // Persist to localStorage for page reload survival
            localStorage.setItem('solushipx_emulation', JSON.stringify({
                isEmulating: true,
                emulationType: targetRole === 'customer' ? 'customer' : 'role',
                isUserEmulation: hasUserData,
                emulatedUser: syntheticEmulatedUser,
                emulatedUserRole: targetRole,
                actualUserEmail: user?.email,
                actualUserRole: userRole,
                emulatedCompanyId: companyData?.companyID,
                emulatedCompanyName: companyData?.name,
                // Include ALL connected company IDs for proper data filtering
                connectedCompanyIds: allConnectedCompanyIds,
                emulatedCustomerId: customerData?.id,
                emulatedCustomerName: customerData?.name,
                emulatedUserName: displayName,
                emulatedUserEmail: displayEmail,
                // Include connected customers for filtering
                connectedCustomers: connectedCustomersData
            }));

            DEBUG && console.log('🎭 Started emulating role:', targetRole,
                targetRole === 'customer' ? `for customer: ${customerData?.name}` : `at company: ${companyData?.name}`);

            // Determine redirect based on emulated role's permissions
            const emulatedHasAdminAccess = hasPermission(targetRole, PERMISSIONS.VIEW_ADMIN_DASHBOARD);
            const redirectTo = '/admin'; // All roles route to the /admin universe

            DEBUG && console.log('🔄 Emulated role admin access:', emulatedHasAdminAccess, '-> redirect to:', redirectTo);

            return { success: true, redirectTo, hasAdminAccess: emulatedHasAdminAccess };
        } catch (error) {
            console.error('Error starting role emulation:', error);
            return { success: false, redirectTo: null };
        }
    };

    /**
     * Stop emulating and return to actual user
     * Handles smart redirects based on current page and actual role permissions
     */
    const stopEmulation = () => {
        if (!isEmulating) return;

        // Capture current state before clearing
        const currentPath = window.location.pathname;
        const isOnAdminPage = currentPath.startsWith('/admin');
        const savedActualUserRole = actualUserRole;

        DEBUG && console.log('🎭 Stopping emulation...');
        DEBUG && console.log('📍 Current path:', currentPath);
        DEBUG && console.log('🔐 Actual user role:', savedActualUserRole);

        // Clear emulation state
        setEmulatedUser(null);
        setEmulatedUserRole(null);
        setIsEmulating(false);
        setActualUser(null);
        setActualUserRole(null);

        // Clear from localStorage
        localStorage.removeItem('solushipx_emulation');

        // #4: restore the admin's pre-emulation selected company and drop the emulated
        // session's selection so the two sessions never share company state.
        const savedAdminCompany = localStorage.getItem('solushipx_admin_selected_company');
        if (savedAdminCompany) {
            localStorage.setItem('solushipx_selected_company_id', savedAdminCompany);
            localStorage.removeItem('solushipx_admin_selected_company');
        } else {
            localStorage.removeItem('solushipx_selected_company_id');
        }

        // Determine redirect based on current page and actual user's permissions
        const actualHasAdminAccess = hasPermission(savedActualUserRole, PERMISSIONS.VIEW_ADMIN_DASHBOARD);

        if (isOnAdminPage) {
            // On admin page - if actual role has admin access, stay on same page (reload)
            // If not, redirect to frontend dashboard
            if (actualHasAdminAccess) {
                DEBUG && console.log('🔄 Staying on admin page (actual role has admin access)');
                window.location.reload();
            } else {
                DEBUG && console.log('🔄 Redirecting to /admin (/dashboard retired)');
                window.location.href = '/admin';
            }
        } else {
            // On frontend page - if actual role has admin access, redirect to admin
            // If not, stay on current page (reload)
            if (actualHasAdminAccess) {
                DEBUG && console.log('🔄 Redirecting to /admin (actual role has admin access)');
                window.location.href = '/admin';
            } else {
                DEBUG && console.log('🔄 Staying on frontend page (actual role lacks admin access)');
                window.location.reload();
            }
        }
    };

    // Get the effective user (emulated or actual)
    const effectiveUser = isEmulating ? emulatedUser : user;
    const effectiveUserRole = isEmulating ? emulatedUserRole : userRole;

    const value = {
        // Use effective user/role (respects emulation)
        currentUser: effectiveUser,
        userRole: effectiveUserRole,
        user: effectiveUser, // Alias for convenience
        loading,
        error,
        initialized,
        roleServiceInitialized,
        isAuthenticated: !!user,

        // Emulation state and functions
        isEmulating,
        emulatedUser,
        actualUser: isEmulating ? actualUser : user,
        actualUserRole: isEmulating ? actualUserRole : userRole,
        startEmulation,
        startRoleEmulation,
        stopEmulation,
        getRoleLevel,
        canEmulateRole,
        getEmulatableRoles,

        // Connected companies for the effective user — exposed so dialogs
        // (e.g. QuickShipCarrierDialog) can scope dropdowns to companies the
        // current user is permitted to act on. Returned as a normalized array
        // of company IDs regardless of whether the underlying user doc stores
        // `connectedCompanies` as `{ companies: [...] }` or as a raw array.
        // Includes the companies an organization MEMBER grant reaches (full
        // company rights); view-grant companies are read-only, so they are left
        // out of this act-on list.
        connectedCompanies: (() => {
            const cc = effectiveUser?.connectedCompanies;
            const direct = !cc ? [] : (Array.isArray(cc) ? cc : (Array.isArray(cc.companies) ? cc.companies : []));
            const orgMember = orgAccessCompanyIds(effectiveUser).member;
            if (orgMember.length === 0) return direct;
            return [...direct, ...orgMember.filter(id => !direct.includes(id))];
        })(),

        // Connected customers for the effective user — exposed so views can
        // apply customer-level access scoping on top of company scoping.
        // Normalized to an array of customer ids/codes regardless of whether the
        // user doc stores `connectedCustomers` as `{ customers: [...] }` or a
        // bare array. Empty => no restriction (full company access).
        connectedCustomers: (() => {
            const cc = effectiveUser?.connectedCustomers;
            if (!cc) return [];
            if (Array.isArray(cc)) return cc;
            if (Array.isArray(cc.customers)) return cc.customers;
            return [];
        })(),

        // A Google session with no profile that may finish a self-serve account.
        needsSetup: Boolean(user && user.needsSetup),

        // Auth functions
        login,
        logout,
        resetPassword,
        signInWithGoogle,
        signInWithMicrosoft,

        // Self-serve accounts
        requestSelfServeAccount,
        completeSelfServeAccount,
        enterNewAccount
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}; 